// Shared v10 primitives: medallion portrait, ORDER strip, buttons, labels.
// Every colour comes from the live palette (useV10) so the dark/light switch
// in Settings re-skins them without a reload.
import React from 'react';
import { Image, Pressable, ScrollView, StyleSheet, Text, TextInput, View, type StyleProp, type TextInputProps, type TextStyle, type ViewStyle } from 'react-native';
import type { GearId } from '../olmps/ascension';
import { GEAR_LABEL, GEAR_ORDER, portraitFor } from './roster';
import { DISPLAY_FONT, makeStyles, useV10 } from './theme';
import type { LucideIcon } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';

export function Kicker({ children, color, style }: { children: React.ReactNode; color?: string; style?: object }) {
  const t = useV10();
  const styles = useStyles();
  return <Text style={[styles.kicker, { color: color ?? t.gold }, style]}>{children}</Text>;
}

export function Display({ children, size = 26, style, lines }: { children: React.ReactNode; size?: number; style?: object; lines?: number }) {
  const styles = useStyles();
  return (
    <Text numberOfLines={lines} style={[styles.display, { fontSize: size, lineHeight: size * 1.18 }, style]}>
      {children}
    </Text>
  );
}

/** Circular deity portrait with a gold (or malady-red) ring. */
export function Medallion({
  id,
  size = 64,
  state = 'active',
  glow = false,
}: {
  id: string;
  size?: number;
  state?: 'active' | 'secured' | 'veiled' | 'malady';
  glow?: boolean;
}) {
  const t = useV10();
  const styles = useStyles();
  const src = portraitFor(id);
  const ring = state === 'malady' ? t.maladyRed : state === 'veiled' ? t.ivoryFaint : t.gold;
  return (
    <View
      style={[
        styles.medal,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          borderColor: ring,
          borderWidth: Math.max(2, size / 28),
        },
        glow && ({ boxShadow: `0 0 ${size / 3}px ${t.goldGlow}` } as object),
      ]}
    >
      {src ? (
        <Image
          source={src}
          style={[
            { width: size * 1.7, height: size * 2.27, marginTop: size * 0.02 },
            state === 'veiled' && { opacity: 0.25 },
            state === 'malady' && { opacity: 0.55 },
          ]}
          resizeMode="cover"
        />
      ) : null}
      {state === 'malady' ? <View style={[StyleSheet.absoluteFill, { backgroundColor: 'rgba(60,10,12,0.35)' }]} /> : null}
    </View>
  );
}

/** O · R · D · E · R — the process control. Highlights the current phase. */
export function OrderStrip({
  current,
  onPress,
  compact = false,
}: {
  current: GearId;
  onPress?: (g: GearId) => void;
  compact?: boolean;
}) {
  const t = useV10();
  const styles = useStyles();
  return (
    <View style={styles.orderRow}>
      {GEAR_ORDER.map((g) => {
        const on = g === current;
        return (
          <Pressable key={g} onPress={onPress ? () => onPress(g) : undefined} style={styles.orderCell} hitSlop={6}>
            <View style={[styles.orderDot, on && styles.orderDotOn]}>
              <Text style={[styles.orderLetter, on && styles.orderLetterOn]}>{GEAR_LABEL[g].letter}</Text>
            </View>
            {!compact ? <Text style={[styles.orderName, on && { color: t.goldBright }]}>{GEAR_LABEL[g].name}</Text> : null}
          </Pressable>
        );
      })}
    </View>
  );
}

export function GoldButton({
  label,
  onPress,
  ghost = false,
  disabled = false,
  compact = false,
  style,
}: {
  label: string;
  onPress: () => void;
  ghost?: boolean;
  disabled?: boolean;
  compact?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  const t = useV10();
  const styles = useStyles();
  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      style={({ pressed }) => [styles.btn, compact && styles.btnCompact, ghost ? styles.btnGhost : styles.btnSolid, pressed && { opacity: 0.8 }, disabled && { opacity: 0.45 }, style]}
    >
      <Text style={[styles.btnText, compact && { fontSize: 12.5 }, ghost ? { color: t.goldBright } : { color: t.onGold }]}>{label}</Text>
    </Pressable>
  );
}

/** Gradient glass fill + inner top highlight, for hand-rolled panels
 *  (Pressables, modal sheets). Place as the first child of a container with
 *  overflow:'hidden'; `tone` 'strong' is near-opaque for modals, 'gold' for
 *  active surfaces. */
export function GlassFill({ tone = 'glass', radius = 22 }: { tone?: 'glass' | 'strong' | 'gold'; radius?: number }) {
  const t = useV10();
  const styles = useStyles();
  const colors = tone === 'gold' ? [t.goldBright, t.gold] : tone === 'strong' ? [t.navyRaised, t.navy] : t.panelGradient;
  return (
    <>
      <LinearGradient colors={colors as [string, string]} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
      <View pointerEvents="none" style={[styles.cardHighlight, { borderTopLeftRadius: radius, borderTopRightRadius: radius }, tone === 'gold' && { backgroundColor: 'rgba(255,255,255,0.35)' }]} />
    </>
  );
}

/** Glass panel with depth: gradient fill, gold hairline, inner top highlight,
 *  soft glow. Never a flat fill. `style` may override padding/radius/border. */
export function GlassCard({ children, style, strong = false }: { children: React.ReactNode; style?: StyleProp<ViewStyle>; strong?: boolean }) {
  const t = useV10();
  const styles = useStyles();
  const flat = StyleSheet.flatten(style) || {};
  const radius = (flat.borderRadius as number | undefined) ?? 22;
  return (
    <View style={[styles.card, { borderRadius: radius }, style, { backgroundColor: 'transparent', overflow: 'hidden' }]}>
      <LinearGradient
        colors={strong ? [t.glassStrong, t.glassStrong] : t.panelGradient}
        start={{ x: 0.2, y: 0 }}
        end={{ x: 0.8, y: 1 }}
        style={StyleSheet.absoluteFill}
        pointerEvents="none"
      />
      <View pointerEvents="none" style={[styles.cardHighlight, { borderTopLeftRadius: radius, borderTopRightRadius: radius }]} />
      {children}
    </View>
  );
}

/** Page background: vertical gradient + a soft gold radiance at the top. Use
 *  behind any screen that does not show the Mountain. */
export function Atmos({ children, style }: { children?: React.ReactNode; style?: StyleProp<ViewStyle> }) {
  const t = useV10();
  return (
    <View style={[{ flex: 1 }, style]}>
      <LinearGradient colors={t.pageGradient} style={StyleSheet.absoluteFill} pointerEvents="none" />
      <LinearGradient
        colors={[t.goldGlow.replace(/[\d.]+\)$/, '0.14)'), 'rgba(0,0,0,0)']}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 0.45 }}
        style={StyleSheet.absoluteFill}
        pointerEvents="none"
      />
      {children}
    </View>
  );
}

// ---- Screen-level primitives (used by every restyled tool) -----------------

/** Page background for a tool screen: the night page, optional scroll. */
export function Screen({ children, scroll = true, style, contentStyle }: { children: React.ReactNode; scroll?: boolean; style?: StyleProp<ViewStyle>; contentStyle?: StyleProp<ViewStyle> }) {
  const styles = useStyles();
  if (!scroll) return <Atmos style={style}>{children}</Atmos>;
  return (
    <Atmos style={style}>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={[styles.screenContent, contentStyle]} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
        {children}
      </ScrollView>
    </Atmos>
  );
}

/** Serif caps section heading with an optional right-side action. */
export function SectionTitle({ children, right, style }: { children: React.ReactNode; right?: React.ReactNode; style?: StyleProp<ViewStyle> }) {
  const styles = useStyles();
  return (
    <View style={[styles.sectionRow, style]}>
      <Text style={styles.sectionTitle}>{children}</Text>
      {right}
    </View>
  );
}

/** Hairline divider. */
export function Divider({ style }: { style?: StyleProp<ViewStyle> }) {
  const styles = useStyles();
  return <View style={[styles.divider, style]} />;
}

/** Selectable chip (filters, enum pickers). Active = gold fill, night text. */
export function Chip({ label, active = false, onPress, color, style, small = false, disabled = false }: { label: string; active?: boolean; onPress?: () => void; color?: string; style?: StyleProp<ViewStyle>; small?: boolean; disabled?: boolean }) {
  const t = useV10();
  const styles = useStyles();
  const tint = color ?? t.gold;
  return (
    <Pressable onPress={disabled ? undefined : onPress} disabled={disabled} hitSlop={4} style={({ pressed }) => [styles.chip, small && styles.chipSmall, { borderColor: active ? tint : t.navyLine, backgroundColor: active ? tint : 'transparent' }, active && ({ boxShadow: `0 0 12px ${t.goldGlow}` } as object), pressed && { opacity: 0.8 }, disabled && { opacity: 0.45 }, style]}>
      <Text style={[styles.chipText, small && { fontSize: 11.5 }, { color: active ? t.onGold : t.ivorySoft }]}>{label}</Text>
    </Pressable>
  );
}

/** Round icon button (header actions, row actions). */
export function IconButton({ icon: Icon, onPress, size = 38, color, active = false, label, style }: { icon: LucideIcon; onPress?: () => void; size?: number; color?: string; active?: boolean; label?: string; style?: StyleProp<ViewStyle> }) {
  const t = useV10();
  const styles = useStyles();
  return (
    <Pressable onPress={onPress} hitSlop={8} accessibilityLabel={label} style={({ pressed }) => [styles.iconBtn, { width: size, height: size, borderRadius: size / 2 }, active && { backgroundColor: t.gold, borderColor: t.goldBright }, pressed && { opacity: 0.8 }, style]}>
      <Icon size={Math.round(size * 0.5)} color={active ? t.onGold : color ?? t.ivory} strokeWidth={1.9} />
    </Pressable>
  );
}

/** Text input — always contrasts with its panel: raised fill, gold rim on focus. */
export function Field({ label, style, inputStyle, multiline, ...rest }: TextInputProps & { label?: string; inputStyle?: StyleProp<TextStyle> }) {
  const t = useV10();
  const styles = useStyles();
  const [focus, setFocus] = React.useState(false);
  return (
    <View style={style}>
      {label ? <Text style={styles.fieldLabel}>{label}</Text> : null}
      <TextInput
        placeholderTextColor={t.ivoryFaint}
        {...rest}
        multiline={multiline}
        onFocus={(e) => { setFocus(true); rest.onFocus?.(e); }}
        onBlur={(e) => { setFocus(false); rest.onBlur?.(e); }}
        style={[styles.field, multiline && styles.fieldMulti, focus && { borderColor: t.gold }, inputStyle]}
      />
    </View>
  );
}

/** Label/value line for detail panels. */
export function KeyValue({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) {
  const styles = useStyles();
  return (
    <View style={styles.kv}>
      <Text style={styles.kvLabel}>{label.toUpperCase()}</Text>
      {typeof value === 'string' || typeof value === 'number' ? <Text style={[styles.kvValue, color ? { color } : null]}>{value}</Text> : value}
    </View>
  );
}

/** Centered empty / loading state. */
export function EmptyState({ title, body, action }: { title: string; body?: string; action?: React.ReactNode }) {
  const styles = useStyles();
  return (
    <View style={styles.empty}>
      <Kicker>{title}</Kicker>
      {body ? <Text style={styles.emptyBody}>{body}</Text> : null}
      {action}
    </View>
  );
}

/** Thin progress track (gold fill; pass color for malady/sector). */
export function Track({ pct, color, height = 4, style }: { pct: number; color?: string; height?: number; style?: StyleProp<ViewStyle> }) {
  const t = useV10();
  const styles = useStyles();
  return (
    <View style={[styles.track, { height, borderRadius: height / 2 }, style]}>
      <View style={{ width: `${Math.round(Math.max(0, Math.min(1, pct)) * 100)}%`, height, backgroundColor: color ?? t.gold }} />
    </View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    kicker: { fontSize: 10.5, fontWeight: '800', letterSpacing: 2.2, textTransform: 'uppercase' },
    display: { fontFamily: DISPLAY_FONT, color: t.ivory, letterSpacing: 0.6 },
    medal: { overflow: 'hidden', alignItems: 'center', backgroundColor: t.navy },
    orderRow: { flexDirection: 'row', justifyContent: 'space-between' },
    orderCell: { alignItems: 'center', gap: 4, flex: 1 },
    orderDot: { width: 34, height: 34, borderRadius: 17, borderWidth: 1, borderColor: t.navyLine, alignItems: 'center', justifyContent: 'center' },
    orderDotOn: { backgroundColor: t.gold, borderColor: t.goldBright, boxShadow: `0 0 14px ${t.goldGlow}` } as object,
    orderLetter: { color: t.ivoryFaint, fontWeight: '800', fontSize: 14 },
    orderLetterOn: { color: t.onGold },
    orderName: { color: t.ivoryFaint, fontSize: 9.5, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase' },
    btn: { height: 44, borderRadius: 22, paddingHorizontal: 18, alignItems: 'center', justifyContent: 'center' },
    btnCompact: { height: 34, paddingHorizontal: 14, borderRadius: 17 },
    btnSolid: { backgroundColor: t.gold, boxShadow: `0 6px 18px ${t.goldGlow}, inset 0 1px 0 rgba(255,255,255,0.35)` } as object,
    btnGhost: { borderWidth: 1, borderColor: t.gold, backgroundColor: t.mode === 'dark' ? 'rgba(214,180,106,0.08)' : 'rgba(168,132,63,0.08)' },
    btnText: { fontWeight: '800', fontSize: 14, letterSpacing: 0.4 },
    card: { borderRadius: 22, borderWidth: 1, borderColor: t.navyLine, padding: 16, boxShadow: `0 10px 30px ${t.glowSoft}` } as object,
    cardHighlight: { position: 'absolute', left: 0, right: 0, top: 0, height: 1, backgroundColor: t.panelHighlight },
    screen: { flex: 1, backgroundColor: t.night },
    screenContent: { padding: 16, paddingBottom: 48, gap: 14 },
    sectionRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 },
    sectionTitle: { fontFamily: DISPLAY_FONT, color: t.ivory, fontSize: 17, letterSpacing: 2.5, textTransform: 'uppercase' },
    divider: { height: 1, backgroundColor: t.lineFaint, marginVertical: 8 },
    chip: { borderWidth: 1, borderRadius: 999, paddingHorizontal: 12, paddingVertical: 7, alignSelf: 'flex-start' },
    chipSmall: { paddingHorizontal: 9, paddingVertical: 4 },
    chipText: { fontSize: 13, fontWeight: '700' },
    iconBtn: { alignItems: 'center', justifyContent: 'center', backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine, boxShadow: `0 4px 12px ${t.glowSoft}` } as object,
    fieldLabel: { color: t.gold, fontSize: 10.5, fontWeight: '800', letterSpacing: 1.8, textTransform: 'uppercase', marginBottom: 6 },
    field: { minHeight: 46, borderRadius: 14, backgroundColor: t.navyRaised, borderWidth: 1, borderColor: t.navyLine, paddingHorizontal: 14, paddingVertical: 11, color: t.ivory, fontSize: 15 },
    fieldMulti: { minHeight: 96, textAlignVertical: 'top' },
    kv: { flexDirection: 'row', gap: 10, alignItems: 'flex-start', paddingVertical: 4 },
    kvLabel: { width: 92, color: t.gold, fontSize: 10, fontWeight: '800', letterSpacing: 1.5, paddingTop: 2 },
    kvValue: { flex: 1, color: t.ivory, fontSize: 14 },
    empty: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 28, gap: 12 },
    emptyBody: { color: t.ivorySoft, textAlign: 'center', fontSize: 14, lineHeight: 20 },
    track: { backgroundColor: t.track, overflow: 'hidden', flexGrow: 1 },
  }),
);
